import React from 'react';
import Modal from "react-modal";
import modalStyle from "app/data/common/modalStyle"
import { ReactComponent as AlertIcon } from "images/alert.svg";
import { Form, Formik } from 'formik';
import XGSFormTextarea from 'ui-components/form/textarea/xgsFormTextarea';
import Button, { ButtonThemes } from 'ui-components/button/button';
import XGSFormSelect from "ui-components/form/select/xgsFormSelect";
import { LabelModes } from 'ui-components/molecules/labeled-inputs/labeledInput';
import { ReactComponent as CrossGrayIcon } from "images/cross_gray.svg";
import "./updateTerminal.scss";
import { useMutation } from "@tanstack/react-query";
import TerminalService from "app/data/terminal-details/terminalService";
import { toast } from "react-toastify";
import { ERROR_MESSAGES } from "app/data/common/errorMessages";
import { TerminalState } from 'slices/terminal-summary/TerminalDetailsState';
import { useSelector } from 'react-redux';
import { terminalDetailsSelector } from 'slices/terminal-summary/terminalDetailsSlice';

const reasonOptions = [
  {
    label: "Non Operational Terminal",
    value: "NON_OPERATIONAL_TERMINAL",
  },
  {
    label: "Duplicate Terminal",
    value: "DUPLICATE_TERMINAL",
  },
  {
    label: "Terminal Upgrade",
    value: "TERMINAL_UPGRADE",
  },
  {
    label: "Terminal Relocation",
    value: "TERMINAL_RELOCATION",
  },
  {
    label: "Other",
    value: "OTHER",
  },
]
const DeleteTerminal = ({ showModal, handleClose, terminalName, refetchDeletionRequest }:
  { showModal: boolean, handleClose: () => void, terminalName: string | undefined, refetchDeletionRequest: () => void }) => {
  const terminalService = TerminalService.getInstance();
  const terminalState: TerminalState = useSelector(terminalDetailsSelector);

  const deleteTerminalMutation = useMutation({
    mutationFn: ({ terminalNumber, terminalName, reason, comment }:
      { terminalNumber: string, terminalName: string, reason: string, comment: string | null }) => {
      return terminalService.deleteTerminalRequest(terminalNumber, terminalName, reason, comment)
    },
    onSuccess: () => {
      toast.info("Terminal deletion request submitted successfully");
      handleClose();
      refetchDeletionRequest();
    },
    onError: (e: any) => {
      if (e instanceof Error) {
        toast.error(e.message);
      }
      else if (e?.response?.data?.message) {
        toast.error(e.response.data.message);
      }
      else {
        toast.error(ERROR_MESSAGES.COMMON);
      }
    },
  });

  return <Modal
    isOpen={showModal}
    style={{
      ...modalStyle, content: {
        ...modalStyle.content,
        width: "400px",
        maxWidth: "90vw",
        minWidth: "300px",
      }
    }}
    onRequestClose={() => { }}
    shouldCloseOnOverlayClick={false}
    shouldCloseOnEsc={false}
  >
    <div className="update-terminal">
      <div className="update-terminal__close">
        <CrossGrayIcon onClick={() => handleClose()} />
      </div>
      <div className='update-terminal__container'>
        <AlertIcon />
        <div className='update-terminal__title'>Delete Terminal</div>
        <div className='update-terminal__terminal-name'>{terminalName}</div>
        <div className='update-terminal__reason'>This terminal will be marked for deletion and sent for approval. Please provide a reason for deletion.</div></div>

      <Formik
        initialValues={{ additionalReason: "", reason: "" }}
        onSubmit={(values) => {
          if (terminalState.data?.terminalNumber && terminalState.data?.terminalName) {
            deleteTerminalMutation.mutate({
              terminalNumber: terminalState.data?.terminalNumber.toString(),
              terminalName: terminalState.data?.terminalName,
              reason: values.reason,
              comment: values.reason === "OTHER" ? values.additionalReason : null
            });
          }
        }}
      >
        {({ dirty, values, setFieldValue }) =>
          <Form>
            <div className="update-terminal__form">
              <div className="update-terminal__form__header">
                <XGSFormSelect
                  name="reason"
                  options={reasonOptions}
                  label='Reason Category'
                  required={true}
                  placeholder="Select a reason..."
                  formik
                  onValueChange={(option) => setFieldValue("reason", option?.value)}
                  className="update-terminal__form__header__select"
                  labelMode={LabelModes.column}
                  value={values?.reason ? reasonOptions.find(
                    (option) => option.value === values.reason
                  ) : null}
                />
              </div>
              <div className="update-terminal__form__content">
                <div className="update-terminal__form__content__form">
                  <XGSFormTextarea
                    name="additionalReason"
                    label='Reason for Deletion'
                    placeholder="Write your reason here..."
                    requiredAsteriskDisabled
                    disabled={values.reason !== "OTHER"}
                  />
                  <div className="update-terminal__form__content__form__reason__footer">
                    This reason will be visible to approvers in Pending Requests.
                  </div>
                  <div className="update-terminal__form__content__edit__button">
                    <Button
                      theme={ButtonThemes.gray}
                      type="button"
                      className="update-terminal__form__content__edit__button__cancel"
                      onClick={() => {
                        handleClose();
                      }}
                      spinner={deleteTerminalMutation.isLoading}
                    >
                      Cancel
                    </Button>
                    <Button
                      theme={ButtonThemes.blue}
                      type="submit"
                      className="update-terminal__form__content__edit__button__submit"
                      spinner={deleteTerminalMutation.isLoading}
                    >
                      Submit for Approval
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </Form>}
      </Formik>
    </div>
  </Modal >;
};

export default DeleteTerminal;
